import { Dialog, DialogTitle, IconButton, DialogContent, TextField, DialogActions, Button } from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";

import React from "react";

type WithdrawMoneyDialogProps = {
  open: boolean;
  accent: string;
  isLoading: boolean;
  onClose: () => void;
  onSubmit: (amount: number, reason: string) => void;
};

type WithdrawMoneyDialogState = {
  amount: string;
  reason: string;
};

export default class WithdrawMoneyDialog extends React.Component<WithdrawMoneyDialogProps, WithdrawMoneyDialogState> {
  constructor(props: WithdrawMoneyDialogProps) {
    super(props);
    this.state = { amount: "", reason: "" };
    this.handleSubmit = this.handleSubmit.bind(this);
  }

  componentDidUpdate(prevProps: WithdrawMoneyDialogProps) {
    if (!prevProps.open && this.props.open) this.setState({ amount: "", reason: "" });
  }

  private parseMoney(input: string) {
    const normalized = (input || "").trim().replace(",", ".");
    const n = Number(normalized);
    if (!Number.isFinite(n)) return null;
    return n;
  }

  private handleSubmit() {
    const amount = this.parseMoney(this.state.amount);
    const reason = (this.state.reason || "").trim();
    if (amount === null || amount <= 0) return;
    if (!reason) return;
    this.props.onSubmit(amount, reason);
  }

  render() {
    const { open, onClose, accent, isLoading } = this.props;
    const amountParsed = this.parseMoney(this.state.amount);
    const canSubmit = amountParsed !== null && amountParsed > 0 && (this.state.reason || "").trim().length > 0 && !isLoading;

    return (
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: 4,
            border: `1px solid ${accent}33`,
            boxShadow: "0 18px 60px rgba(0,0,0,.22)",
            overflow: "hidden",
          },
        }}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: "rgba(0,0,0,.45)",
              backdropFilter: "blur(5px)",
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 900,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            py: 2,
            px: 2.5,
            borderBottom: "1px solid rgba(0,0,0,0.06)",
            background: "linear-gradient(180deg, rgba(197,38,38,0.08), rgba(255,255,255,0))",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 34,
                height: 34,
                borderRadius: 12,
                background: `${accent}14`,
                border: `1px solid ${accent}33`,
                display: "grid",
                placeItems: "center",
                color: accent,
              }}
            >
              <ReceiptLongIcon fontSize="small" />
            </div>
            Withdraw money
          </div>

          <IconButton size="small" onClick={onClose} aria-label="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ px: 2.5, py: 2.5, display: "grid", gap: 1.6 }}>
          <TextField
            autoFocus
            label="Amount (€)"
            type="number"
            inputProps={{ step: "0.01", min: "0" }}
            value={this.state.amount}
            onChange={(e) => this.setState({ amount: e.target.value })}
            fullWidth
            helperText="This will be recorded as a negative transaction"
            sx={{
              "& .MuiOutlinedInput-root": { borderRadius: 3 },
              "& .MuiOutlinedInput-notchedOutline": { borderColor: `${accent}40` },
              "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: accent },
            }}
          />

          <TextField
            label="Reason"
            value={this.state.reason}
            onChange={(e) => this.setState({ reason: e.target.value })}
            fullWidth
            required
            placeholder="e.g. Team snacks / supplies"
            helperText="Tell us what this expense is for"
            sx={{
              "& .MuiOutlinedInput-root": { borderRadius: 3 },
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(0,0,0,0.12)" },
              "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: accent },
            }}
          />
        </DialogContent>

        <DialogActions sx={{ px: 2.5, pb: 2.5, pt: 0, gap: 1 }}>
          <Button
            onClick={onClose}
            sx={{
              textTransform: "none",
              borderRadius: 3,
              px: 2,
              border: `1px solid ${accent}55`,
              color: "#1a1a1a",
            }}
            variant="outlined"
            disabled={isLoading}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={this.handleSubmit}
            disabled={!canSubmit}
            sx={{
              textTransform: "none",
              fontWeight: 900,
              borderRadius: 3,
              px: 2.2,
              backgroundColor: "#c52626ff",
              "&:hover": { backgroundColor: "#a71f1fff" },
            }}
          >
            Withdraw
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
}